import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Eye, EyeOff, Lock, LogOut, LayoutDashboard, Images, Clock, Trash2, Plus,
  UploadCloud, ArrowLeft, Star, X, GripVertical, RotateCcw, ExternalLink, Check, ShieldCheck,
} from "lucide-react";
import logo from "@/assets/logo_ethos.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILE = (u) => (u?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const TOKEN_KEY = "ethos_admin_token";
const CATEGORIES = ["Cultural Festival", "Community", "Arts", "Celebration", "Panel", "Literacy", "Youth"];
const STATUSES = ["draft", "published", "unpublished", "archived"];
const CONSENTS = [
  { v: "approved", l: "Approved for Public Use" },
  { v: "pending", l: "Pending Approval" },
  { v: "internal", l: "Internal Use Only" },
  { v: "do_not_publish", l: "Do Not Publish" },
];
const STATUS_COLOR = { published: "#0f766e", draft: "#64748b", unpublished: "#c9a84c", archived: "#be185d" };

const api = (token) => axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} });

export default function GalleryManage() {
  const [token, setToken] = useState(sessionStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    api(token).get("/admin/me").then(() => setChecking(false)).catch(() => { sessionStorage.removeItem(TOKEN_KEY); setToken(""); setChecking(false); });
  }, [token]);

  const login = (t) => { sessionStorage.setItem(TOKEN_KEY, t); setToken(t); };
  const logout = () => { sessionStorage.removeItem(TOKEN_KEY); setToken(""); toast.success("Logged out"); };

  if (checking) return <div className="min-h-screen bg-ink" />;
  if (!token) return <LoginScreen onLogin={login} />;
  return <Dashboard token={token} onLogout={logout} />;
}

function LoginScreen({ onLogin }) {
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [lockLeft, setLockLeft] = useState(0);

  useEffect(() => {
    if (lockLeft <= 0) return;
    const t = setTimeout(() => setLockLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [lockLeft]);

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const submit = async (e) => {
    e.preventDefault();
    if (lockLeft > 0) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api().post("/admin/login", { password: pw });
      onLogin(data.token);
    } catch (e2) {
      if (e2?.response?.status === 429) {
        const ra = parseInt(e2.response.headers?.["retry-after"] || "0", 10);
        setLockLeft(ra > 0 ? ra : 900);
        setErr("Too many attempts.");
      } else {
        setErr(e2?.response?.data?.detail || "Incorrect password");
      }
    } finally { setBusy(false); }
  };

  const locked = lockLeft > 0;

  return (
    <div className="min-h-screen bg-ink crosshatch radial-glow flex items-center justify-center px-6" data-testid="admin-login">
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center">
          <img src={logo} alt="Ethos Foundation" className="h-14 mx-auto" />
        </div>
        <form onSubmit={submit} className="bg-ink2 border border-white/10 p-8 mt-8" style={{ borderRadius: 2 }}>
          <div className="flex items-center gap-2 text-teal justify-center">
            <Lock size={16} /><span className="font-body-eth text-[11px] uppercase tracking-[0.2em] text-glow">Gallery Management</span>
          </div>
          <h1 className="font-serif-eth font-light text-white text-3xl text-center mt-4">Enter the <em>gallery.</em></h1>
          <div className="relative mt-8">
            <input
              type={show ? "text" : "password"}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Password"
              data-testid="admin-password"
              disabled={locked}
              className="eth-input w-full py-2.5 pr-10 text-white text-lg disabled:opacity-50"
              style={{ borderBottomColor: "rgba(255,255,255,0.2)" }}
              autoFocus
            />
            <button type="button" onClick={() => setShow((s) => !s)} data-testid="toggle-password" className="absolute right-0 top-2.5 text-white/50 hover:text-white">
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {locked && (
            <div className="mt-5 border border-[#c9a84c]/40 bg-[#c9a84c]/10 px-4 py-3 flex items-center gap-3" style={{ borderRadius: 2 }} data-testid="lockout-countdown">
              <Lock size={16} className="text-gold shrink-0" />
              <div className="font-body-eth text-sm text-white/80">
                Locked after too many attempts. Try again in <span className="text-gold font-semibold tabular-nums">{mmss(lockLeft)}</span>
              </div>
            </div>
          )}
          {err && !locked && <div className="font-body-eth text-sm text-[#f87171] mt-4" data-testid="login-error">{err}</div>}
          <button type="submit" disabled={busy || locked} data-testid="enter-gallery-btn" className="btn-teal w-full mt-7 font-body-eth text-sm font-semibold py-3 disabled:opacity-60">
            {locked ? `Locked · ${mmss(lockLeft)}` : busy ? "Checking..." : "Enter Gallery"}
          </button>
        </form>
        <a href="/media" className="block text-center font-body-eth text-white/40 text-xs mt-6 hover:text-glow">← Back to public site</a>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="bg-white border border-[var(--line)] p-5" style={{ borderRadius: 2 }} data-testid={testid}>
      <div className="font-serif-eth text-4xl text-ink">{value ?? "—"}</div>
      <div className="font-body-eth text-xs uppercase tracking-wider text-mute mt-1">{label}</div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const c = api(token);
  const [view, setView] = useState("dashboard");
  const [editId, setEditId] = useState(null);
  const [stats, setStats] = useState({});
  const [albums, setAlbums] = useState([]);

  const loadDash = useCallback(() => c.get("/admin/dashboard").then((r) => setStats(r.data)).catch(() => {}), [token]);
  const loadAlbums = useCallback(() => c.get("/admin/albums").then((r) => setAlbums(r.data)).catch(() => {}), [token]);

  useEffect(() => { loadDash(); loadAlbums(); }, [view]);

  const openEditor = (id) => { setEditId(id); setView("editor"); };

  const createAlbum = async () => {
    try {
      const { data } = await c.post("/admin/albums", { name: "Untitled Album", category: "Community", status: "draft" });
      toast.success("Album created");
      openEditor(data.id);
    } catch { toast.error("Could not create album"); }
  };

  const NAV = [
    { k: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { k: "albums", label: "Manage Albums", icon: Images },
    { k: "pending", label: "Review Pending", icon: Clock },
    { k: "trash", label: "Trash", icon: Trash2 },
  ];

  return (
    <div className="min-h-screen bg-sand flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-60 bg-ink crosshatch min-h-screen p-5 flex flex-col fixed">
        <img src={logo} alt="Ethos" className="h-9 self-start" />
        <nav className="mt-10 flex flex-col gap-1">
          {NAV.map((n) => (
            <button key={n.k} onClick={() => setView(n.k)} data-testid={`nav-${n.k}`}
              className={`flex items-center gap-3 font-body-eth text-sm px-3 py-2.5 text-left transition-colors ${view === n.k ? "bg-teal text-white" : "text-white/60 hover:text-white"}`} style={{ borderRadius: 2 }}>
              <n.icon size={16} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-1">
          <a href="/media" target="_blank" rel="noreferrer" className="flex items-center gap-3 font-body-eth text-sm px-3 py-2.5 text-white/60 hover:text-white" style={{ borderRadius: 2 }}>
            <ExternalLink size={16} /> View Public Gallery
          </a>
          <button onClick={onLogout} data-testid="logout-btn" className="flex items-center gap-3 font-body-eth text-sm px-3 py-2.5 text-white/60 hover:text-white" style={{ borderRadius: 2 }}>
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-60 p-8 lg:p-12">
        {view === "dashboard" && (
          <div>
            <h1 className="font-serif-eth font-light text-ink text-4xl">Gallery <em>overview.</em></h1>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-8">
              <StatCard label="Total Albums" value={stats.total_albums} testid="stat-total-albums" />
              <StatCard label="Published" value={stats.published_albums} testid="stat-published" />
              <StatCard label="Drafts" value={stats.draft_albums} testid="stat-draft" />
              <StatCard label="Total Photos" value={stats.total_photos} testid="stat-photos" />
              <StatCard label="Pending Approval" value={stats.pending_approval} testid="stat-pending" />
              <StatCard label="In Trash" value={stats.trash} testid="stat-trash" />
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={createAlbum} data-testid="create-album-btn" className="btn-teal font-body-eth text-sm font-semibold px-6 py-3 inline-flex items-center gap-2"><Plus size={16} /> Create New Album</button>
              <button onClick={() => setView("albums")} className="btn-navy font-body-eth text-sm font-semibold px-6 py-3 inline-flex items-center gap-2"><Images size={16} /> Manage Albums</button>
              <button onClick={() => setView("pending")} className="font-body-eth text-sm font-semibold px-6 py-3 border border-[var(--line)] bg-white inline-flex items-center gap-2" style={{ borderRadius: 2 }}><Clock size={16} /> Review Pending {stats.pending_approval ? `(${stats.pending_approval})` : ""}</button>
            </div>
            <div className="mt-6 flex items-center gap-2 font-body-eth text-xs text-mute"><ShieldCheck size={14} className="text-teal" /> Uploads, edits, and deletes are protected on the server. Access ends when you close the browser.</div>
          </div>
        )}

        {view === "albums" && <AlbumsList albums={albums} onOpen={openEditor} onCreate={createAlbum} reload={loadAlbums} c={c} />}
        {view === "editor" && <AlbumEditor id={editId} c={c} onBack={() => { setView("albums"); }} />}
        {view === "pending" && <PendingReview c={c} />}
        {view === "trash" && <TrashView c={c} />}
      </main>
    </div>
  );
}

function AlbumsList({ albums, onOpen, onCreate, reload, c }) {
  const setStatus = async (id, status) => { await c.patch(`/admin/albums/${id}/status`, { status }); toast.success(`Album ${status}`); reload(); };
  const del = async (a) => { if (!window.confirm(`Move "${a.name}" to Trash? You can restore it within 30 days.`)) return; await c.delete(`/admin/albums/${a.id}`); toast.success("Moved to Trash"); reload(); };

  return (
    <div data-testid="albums-list">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-eth font-light text-ink text-4xl">Manage <em>albums.</em></h1>
        <button onClick={onCreate} data-testid="create-album-btn-2" className="btn-teal font-body-eth text-sm font-semibold px-6 py-3 inline-flex items-center gap-2"><Plus size={16} /> New Album</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {albums.map((a) => (
          <div key={a.id} className="bg-white border border-[var(--line)] overflow-hidden" style={{ borderRadius: 2 }} data-testid={`admin-album-${a.id}`}>
            <div className="relative h-40 bg-ink">
              {a.cover ? <img src={FILE(a.cover)} alt={a.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/30"><Images size={28} /></div>}
              <span className="absolute top-2 left-2 font-body-eth text-[10px] uppercase tracking-wider px-2 py-0.5 text-white" style={{ background: STATUS_COLOR[a.status], borderRadius: 2 }}>{a.status}</span>
            </div>
            <div className="p-4">
              <h3 className="font-serif-eth text-xl text-ink truncate">{a.name}</h3>
              <div className="font-body-eth text-xs text-mute mt-0.5">{a.category} · {a.count} photos</div>
              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={() => onOpen(a.id)} data-testid={`edit-${a.id}`} className="btn-navy font-body-eth text-xs font-semibold px-3 py-2">Edit</button>
                {a.status !== "published" ? (
                  <button onClick={() => setStatus(a.id, "published")} data-testid={`publish-${a.id}`} className="btn-teal font-body-eth text-xs font-semibold px-3 py-2">Publish</button>
                ) : (
                  <button onClick={() => setStatus(a.id, "unpublished")} data-testid={`unpublish-${a.id}`} className="font-body-eth text-xs font-semibold px-3 py-2 border border-[var(--line)]" style={{ borderRadius: 2 }}>Unpublish</button>
                )}
                <button onClick={() => del(a)} data-testid={`delete-${a.id}`} className="font-body-eth text-xs font-semibold px-3 py-2 text-[#be185d] border border-[#be185d]/30" style={{ borderRadius: 2 }}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {albums.length === 0 && <div className="font-body-eth text-mute mt-8">No albums yet. Create your first album.</div>}
    </div>
  );
}

function AlbumEditor({ id, c, onBack }) {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState([]);
  const inputRef = useRef(null);
  const dragIdx = useRef(null);
  const MAX_MB = 25;

  const load = useCallback(() => c.get(`/admin/albums/${id}`).then((r) => setA(r.data)).catch(() => toast.error("Could not load album")), [id]);
  useEffect(() => { load(); }, [id]);

  if (!a) return <div className="font-body-eth text-mute">Loading…</div>;

  const setField = (k, v) => setA((cur) => ({ ...cur, [k]: v }));
  const setPhoto = (pid, k, v) => setA((cur) => ({ ...cur, photos: cur.photos.map((p) => (p.id === pid ? { ...p, [k]: v } : p)) }));

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name: a.name, description: a.description, category: a.category, date: a.date,
        location: a.location, status: a.status, cover: a.cover,
        photos: a.photos.map((p, i) => ({ id: p.id, caption: p.caption, alt: p.alt, consent: p.consent, order: i })),
      };
      const { data } = await c.put(`/admin/albums/${id}`, body);
      setA(data);
      toast.success("Saved");
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const upload = async (fileList) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    const tooBig = files.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (tooBig) { toast.error(`${tooBig.name} is over ${MAX_MB}MB`); return; }
    setUploading(true);
    setProgress(files.map((f) => ({ name: f.name, pct: 0, status: "uploading" })));
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      const fd = new FormData();
      fd.append("files", files[i]);
      try {
        await c.post(`/admin/albums/${id}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setProgress((cur) => cur.map((p, idx) => (idx === i ? { ...p, pct, status: pct >= 100 ? "processing" : "uploading" } : p)));
          },
        });
        ok++;
        setProgress((cur) => cur.map((p, idx) => (idx === i ? { ...p, pct: 100, status: "done" } : p)));
      } catch (e) {
        const msg = e?.response?.data?.detail || "Failed";
        setProgress((cur) => cur.map((p, idx) => (idx === i ? { ...p, status: "error", msg } : p)));
      }
    }
    if (ok) toast.success(`${ok} photo(s) uploaded — marked Pending Approval`);
    await load();
    setUploading(false);
    setTimeout(() => setProgress([]), 2000);
  };

  const delPhoto = async (p) => {
    if (!window.confirm("Move this photo to Trash?")) return;
    await c.delete(`/admin/albums/${id}/photos/${p.id}`);
    toast.success("Photo moved to Trash");
    load();
  };

  const reorder = (from, to) => {
    setA((cur) => {
      const arr = [...cur.photos];
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      return { ...cur, photos: arr };
    });
  };

  return (
    <div data-testid="album-editor">
      <button onClick={onBack} className="inline-flex items-center gap-2 font-body-eth text-sm font-semibold text-teal mb-6"><ArrowLeft size={16} /> Back to Albums</button>
      <div className="grid lg:grid-cols-[340px_1fr] gap-8">
        {/* Meta */}
        <div className="bg-white border border-[var(--line)] p-6 h-max" style={{ borderRadius: 2 }}>
          <h2 className="font-serif-eth text-2xl text-ink mb-4">Album details</h2>
          <label className="font-body-eth text-[11px] uppercase tracking-wider text-mute">Title</label>
          <input value={a.name} onChange={(e) => setField("name", e.target.value)} data-testid="editor-name" className="eth-input w-full py-2 text-ink mb-4 mt-1" />
          <label className="font-body-eth text-[11px] uppercase tracking-wider text-mute">Description</label>
          <textarea value={a.description} onChange={(e) => setField("description", e.target.value)} rows={3} data-testid="editor-description" className="eth-input w-full py-2 text-ink mb-4 mt-1 resize-none" />
          <label className="font-body-eth text-[11px] uppercase tracking-wider text-mute">Program Category</label>
          <select value={a.category} onChange={(e) => setField("category", e.target.value)} data-testid="editor-category" className="eth-input w-full py-2 text-ink mb-4 mt-1 bg-transparent">
            {CATEGORIES.map((x) => <option key={x}>{x}</option>)}
          </select>
          <label className="font-body-eth text-[11px] uppercase tracking-wider text-mute">Event Date</label>
          <input value={a.date} onChange={(e) => setField("date", e.target.value)} placeholder="e.g. September 2025" data-testid="editor-date" className="eth-input w-full py-2 text-ink mb-4 mt-1" />
          <label className="font-body-eth text-[11px] uppercase tracking-wider text-mute">Location</label>
          <input value={a.location} onChange={(e) => setField("location", e.target.value)} data-testid="editor-location" className="eth-input w-full py-2 text-ink mb-4 mt-1" />
          <label className="font-body-eth text-[11px] uppercase tracking-wider text-mute">Status</label>
          <select value={a.status} onChange={(e) => setField("status", e.target.value)} data-testid="editor-status" className="eth-input w-full py-2 text-ink mb-6 mt-1 bg-transparent">
            {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <button onClick={save} disabled={busy} data-testid="save-album-btn" className="btn-teal w-full font-body-eth text-sm font-semibold py-3 disabled:opacity-60">{busy ? "Saving…" : "Save Album"}</button>
        </div>

        {/* Photos */}
        <div>
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
            className="border border-dashed border-[var(--line)] hover:border-teal transition-colors cursor-pointer p-8 text-center bg-white"
            style={{ borderRadius: 2 }} data-testid="editor-dropzone">
            <UploadCloud size={26} className="text-teal mx-auto" />
            <div className="font-body-eth text-sm text-ink mt-2">{uploading ? "Uploading & processing…" : "Click or drag photos to upload"}</div>
            <div className="font-body-eth text-xs text-mute mt-1">JPG, PNG, HEIC, WEBP · auto-resized, compressed & thumbnailed</div>
            <input ref={inputRef} type="file" accept="image/*,.heic,.heif" multiple hidden data-testid="editor-files" onChange={(e) => upload(e.target.files)} />
          </div>

          {progress.length > 0 && (
            <div className="mt-4 flex flex-col gap-2" data-testid="upload-progress">
              {progress.map((p, i) => (
                <div key={i} className="bg-white border border-[var(--line)] px-3 py-2" style={{ borderRadius: 2 }}>
                  <div className="flex items-center justify-between font-body-eth text-xs">
                    <span className="truncate max-w-[65%] text-ink">{p.name}</span>
                    <span className={p.status === "error" ? "text-[#be185d]" : p.status === "done" ? "text-teal" : "text-mute"}>
                      {p.status === "error" ? (p.msg || "Failed") : p.status === "done" ? "Done" : p.status === "processing" ? "Processing…" : `${p.pct}%`}
                    </span>
                  </div>
                  <div className="h-1.5 bg-sand mt-1.5 overflow-hidden" style={{ borderRadius: 2 }}>
                    <div className="h-full transition-[width] duration-200" style={{ width: `${p.status === "error" ? 100 : p.pct}%`, background: p.status === "error" ? "#be185d" : "var(--teal)" }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="font-body-eth text-xs text-mute mt-4 mb-3">Drag to reorder · newly uploaded photos are <b>Pending Approval</b> and stay hidden publicly until you approve them.</div>

          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="editor-photos">
            {a.photos.map((p, i) => (
              <div key={p.id} draggable
                onDragStart={() => (dragIdx.current = i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragIdx.current !== null && dragIdx.current !== i) reorder(dragIdx.current, i); dragIdx.current = null; }}
                className="bg-white border border-[var(--line)] p-2" style={{ borderRadius: 2 }} data-testid={`photo-${p.id}`}>
                <div className="relative">
                  <img src={FILE(p.thumb || p.url)} alt={p.alt} className="w-full h-36 object-cover" style={{ borderRadius: 2 }} />
                  <div className="absolute top-1 left-1 text-white/80 bg-ink/60 p-1" style={{ borderRadius: 2 }}><GripVertical size={13} /></div>
                  <button onClick={() => setField("cover", p.url)} title="Set as cover" data-testid={`cover-${p.id}`} className={`absolute top-1 right-1 p-1.5 ${a.cover === p.url ? "bg-gold text-ink" : "bg-ink/60 text-white"}`} style={{ borderRadius: 2 }}><Star size={13} /></button>
                  <button onClick={() => delPhoto(p)} data-testid={`delphoto-${p.id}`} className="absolute bottom-1 right-1 bg-ink/60 text-white p-1.5" style={{ borderRadius: 2 }}><Trash2 size={13} /></button>
                </div>
                <input value={p.caption || ""} onChange={(e) => setPhoto(p.id, "caption", e.target.value)} placeholder="Caption" className="eth-input w-full py-1.5 text-ink text-sm mt-2" />
                <input value={p.alt || ""} onChange={(e) => setPhoto(p.id, "alt", e.target.value)} placeholder="Alt text (accessibility)" className="eth-input w-full py-1.5 text-ink text-sm mt-1" />
                <select value={p.consent} onChange={(e) => setPhoto(p.id, "consent", e.target.value)} data-testid={`consent-${p.id}`} className="eth-input w-full py-1.5 text-ink text-xs mt-1 bg-transparent">
                  {CONSENTS.map((cs) => <option key={cs.v} value={cs.v}>{cs.l}</option>)}
                </select>
              </div>
            ))}
          </div>
          {a.photos.length === 0 && <div className="font-body-eth text-mute mt-4">No photos yet — upload some above.</div>}
        </div>
      </div>
    </div>
  );
}

function PendingReview({ c }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => c.get("/admin/pending").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, []);

  const setConsent = async (it, consent) => {
    const body = { photos: [{ id: it.photo.id, consent }] };
    await c.put(`/admin/albums/${it.album_id}`, body);
    toast.success(consent === "approved" ? "Approved for public use" : "Updated");
    load();
  };

  return (
    <div data-testid="pending-review">
      <h1 className="font-serif-eth font-light text-ink text-4xl">Review <em>pending photos.</em></h1>
      <p className="font-body-eth text-mute text-sm mt-2">For photos involving children, confirm you have parent/guardian permission before approving.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        {items.map((it) => (
          <div key={it.photo.id} className="bg-white border border-[var(--line)] p-2" style={{ borderRadius: 2 }} data-testid={`pending-${it.photo.id}`}>
            <img src={FILE(it.photo.thumb || it.photo.url)} alt={it.photo.alt} className="w-full h-40 object-cover" style={{ borderRadius: 2 }} />
            <div className="font-body-eth text-xs text-mute mt-2">{it.album_name}</div>
            <div className="flex gap-2 mt-2">
              <button onClick={() => setConsent(it, "approved")} data-testid={`approve-${it.photo.id}`} className="btn-teal flex-1 font-body-eth text-xs font-semibold py-2 inline-flex items-center justify-center gap-1"><Check size={13} /> Approve</button>
              <button onClick={() => setConsent(it, "do_not_publish")} className="font-body-eth text-xs font-semibold py-2 px-3 border border-[#be185d]/40 text-[#be185d]" style={{ borderRadius: 2 }}>Reject</button>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && <div className="font-body-eth text-mute mt-8">Nothing pending. All caught up.</div>}
    </div>
  );
}

function TrashView({ c }) {
  const [data, setData] = useState({ albums: [], photos: [] });
  const load = useCallback(() => c.get("/admin/trash").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, []);

  const restore = async (body) => { await c.post("/admin/trash/restore", body); toast.success("Restored"); load(); };
  const perm = async (body) => { if (!window.confirm("Permanently delete? This cannot be undone.")) return; await c.delete("/admin/trash/permanent", { data: body }); toast.success("Permanently deleted"); load(); };
  const empty = async () => { if (!window.confirm("Empty Trash permanently?")) return; await c.delete("/admin/trash/empty"); toast.success("Trash emptied"); load(); };

  const isEmpty = data.albums.length === 0 && data.photos.length === 0;

  return (
    <div data-testid="trash-view">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-eth font-light text-ink text-4xl">Trash</h1>
        {!isEmpty && <button onClick={empty} data-testid="empty-trash" className="font-body-eth text-sm font-semibold px-5 py-2.5 text-[#be185d] border border-[#be185d]/40" style={{ borderRadius: 2 }}>Empty Trash</button>}
      </div>
      <p className="font-body-eth text-mute text-sm mt-2">Items are kept for 30 days, then permanently removed.</p>

      {data.albums.length > 0 && <h2 className="font-serif-eth text-2xl text-ink mt-8 mb-3">Albums</h2>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {data.albums.map((a) => (
          <div key={a.id} className="bg-white border border-[var(--line)] p-2" style={{ borderRadius: 2 }}>
            {a.cover ? <img src={FILE(a.cover)} alt={a.name} className="w-full h-32 object-cover" style={{ borderRadius: 2 }} /> : <div className="h-32 bg-sand" />}
            <div className="font-body-eth text-sm text-ink mt-2 truncate">{a.name}</div>
            <div className="flex gap-2 mt-2">
              <button onClick={() => restore({ kind: "album", id: a.id })} className="btn-teal flex-1 font-body-eth text-xs font-semibold py-2 inline-flex items-center justify-center gap-1"><RotateCcw size={12} /> Restore</button>
              <button onClick={() => perm({ kind: "album", id: a.id })} className="font-body-eth text-xs font-semibold py-2 px-3 border border-[#be185d]/40 text-[#be185d]" style={{ borderRadius: 2 }}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {data.photos.length > 0 && <h2 className="font-serif-eth text-2xl text-ink mt-8 mb-3">Photos</h2>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {data.photos.map((p) => (
          <div key={p.photo_id} className="bg-white border border-[var(--line)] p-2" style={{ borderRadius: 2 }}>
            <img src={FILE(p.thumb)} alt="" className="w-full h-28 object-cover" style={{ borderRadius: 2 }} />
            <div className="font-body-eth text-xs text-mute mt-1 truncate">{p.album_name}</div>
            <div className="flex gap-2 mt-2">
              <button onClick={() => restore({ kind: "photo", album_id: p.album_id, photo_id: p.photo_id })} className="btn-teal flex-1 font-body-eth text-xs font-semibold py-1.5 inline-flex items-center justify-center gap-1"><RotateCcw size={12} /></button>
              <button onClick={() => perm({ kind: "photo", album_id: p.album_id, photo_id: p.photo_id })} className="font-body-eth text-xs font-semibold py-1.5 px-2 border border-[#be185d]/40 text-[#be185d]" style={{ borderRadius: 2 }}><X size={12} /></button>
            </div>
          </div>
        ))}
      </div>

      {isEmpty && <div className="font-body-eth text-mute mt-8">Trash is empty.</div>}
    </div>
  );
}
